<template>
  <div
    @click.self.stop="actionOnClick"
    class="cursor-pointer pdh-10px pdv-7px height-100 width-100percent"
    ref="besoinInput">
    <!-- Produit achat -->
    <template v-if="field === 'produitAchat'">
      <router-link-blank
        v-if="isDetail && besoin.produitAchat"
        @click.stop
        :to="getLibelleAndRoutesForProduct(besoin.produitAchat)"
        :message="besoin.produitAchat.libelle"
        class="is-inline">
      </router-link-blank>
      <produit-autocomplete
        v-if="!isDetail"
        :value="besoin.produitAchat"
        @input="(event) => {
          updateValue(event, 'produitAchat', index);
          updateValue(event ? event.id : null, 'produitAchatId', index);
        }"
        @click-autocomplete.stop
        :societeActiveId="societeActiveId"
        :sens="constantes.SENS_ACHAT"
        :categories="[constantes.CATEGORIE_IMPRESSION_AFFICHE]"
        :filteredItemFunc="(p) => p.id !== besoin.produitAchatId"
        :disabled="func.besoinIsNotModifiable(besoin)"
        :focus="!isNewBesoin"
        expanded
      />
    </template>

    <!-- Format affiche -->
    <template v-if="field === 'formatAffiche'">
      <router-link-blank
        v-if="isDetail && besoin.formatAffiche"
        :to="{ name: 'c.bodac.format_affiche', params: { id: besoin.formatAffiche.id } }"
        :message="besoin.formatAffiche.libelle"
        class="is-inline">
      </router-link-blank>
      <query-autocomplete
        v-if="!isDetail"
        :value="besoin.formatAffiche"
        @input="updateFormatAfficheValue($event, index)"
        @click-autocomplete.stop
        :filteredItemFunc="(format) => format.id !== besoin.formatAfficheId"
        :asyncFunc="getFormatAfficheData"
        :disabled="func.besoinIsNotModifiable(besoin)"
        :focus="!isNewBesoin"
        open-on-focus
        expanded
      />
    </template>

    <!-- Nature support affiche -->
    <template v-if="field === 'natureSupportAfficheId'">
      <div v-if="isDetail" class="is-inline">
        {{ getNatureSupportAffiche(besoin.natureSupportAfficheId) }}
      </div>
      <parametre-autocomplete
        v-if="!isDetail"
        :value="besoin.natureSupportAfficheId"
        :target="constantes.PARAMETRES_KEYS.natureSupportAffiche"
        @input="updateValue($event || null, 'natureSupportAfficheId', index)"
        :disabled="func.besoinIsNotModifiable(besoin)"
      />
    </template>

    <!-- Type de matériel -->
    <template v-if="field === 'typeMaterielId'">
      <div v-if="isDetail" class="is-inline">
        {{ getTypeMateriel(besoin.typeMaterielId) }}
      </div>
      <parametre-autocomplete
        v-if="!isDetail"
        :value="besoin.typeMaterielId"
        :target="constantes.PARAMETRES_KEYS.materielType"
        @input="updateValue($event || null, 'typeMaterielId', index)"
        :disabled="func.besoinIsNotModifiable(besoin)"
      />
    </template>

    <!-- Longueur -->
    <template v-if="field === 'longueur'">
      <div v-if="isDetail" class="is-inline">
        {{ besoin.longueur }}
      </div>
      <b-input
        v-if="!isDetail"
        :value="besoin.longueur"
        @input="updateValue($event, 'longueur', index)"
        :disabled="func.besoinIsNotModifiable(besoin)"
        :use-html5-validation="false"
        type="number" min="0" step="0.01"
      />
    </template>

    <!-- Largeur -->
    <template v-if="field === 'largeur'">
      <div v-if="isDetail" class="is-inline">
        {{ besoin.largeur }}
      </div>
      <b-input
        v-if="!isDetail"
        :value="besoin.largeur"
        @input="updateValue($event, 'largeur', index)"
        :disabled="func.besoinIsNotModifiable(besoin)"
        :use-html5-validation="false"
        type="number" min="0" step="0.01"
      />
    </template>

    <!-- Produit -->
    <template v-if="field === 'produit'">
      <router-link-blank
        v-if="isDetail && besoin.produit"
        :to="getLibelleAndRoutesForProduct(besoin.produit)"
        :message="besoin.produit.libelle"
        class="is-inline">
      </router-link-blank>
      <query-autocomplete
        v-if="!isDetail"
        :value="besoin.produit"
        @input="(event) => {
          updateValue(event, 'produit', index);
          updateValue(event ? event.id : null, 'produitId', index);
        }"
        @click-autocomplete.stop
        :filteredItemFunc="(produit) => produit.id !== besoin.produitId"
        :asyncFunc="getProduitData"
        :disabled="func.besoinIsNotModifiable(besoin)"
        :focus="!isNewBesoin"
        open-on-focus
        expanded
      />
    </template>

    <!-- Date de départ -->
    <template v-if="field === 'dateDepart'">
      <div v-if="isDetail" class="is-inline">
        {{ func.formatDate(besoin.dateDepart) }}
      </div>
      <b-datepicker
        v-if="!isDetail"
        :value="besoin.dateDepart"
        @input="updateDateDepartValue($event, index)"
        @click.native.stop
        :disabled="func.besoinIsNotModifiable(besoin)"
        ref="datepickerDateDepart"
        icon="calendar-today"
        position="is-bottom-right"
        nearby-selectable-month-days
        show-week-number editable>
      </b-datepicker>
    </template>

    <!-- Nb d'affiches vendues -->
    <template v-if="field === 'nbAfficheVendue'">
      <div v-if="isDetail" class="is-pulled-right is-inline">
        {{ besoin.nbAfficheVendue }}
      </div>
      <b-input
        v-if="!isDetail"
        :value="besoin.nbAfficheVendue"
        @input="updateNbAfficheVendueValue($event, index)"
        @click.native.stop
        :disabled="func.besoinIsNotModifiable(besoin)"
        :use-html5-validation="false"
        type="number" min="0" step="1"
        ref="inputNbAfficheVendue"
      />
    </template>

    <!-- Nb d'affiches maintenance -->
    <template v-if="field === 'nbAfficheMaintenance'">
      <div v-if="isDetail" class="is-pulled-right is-inline">
        {{ besoin.nbAfficheMaintenance }}
      </div>
      <b-input
        v-if="!isDetail"
        :value="besoin.nbAfficheMaintenance"
        @input="updateNbAfficheMaintenanceValue($event, index)"
        @click.native.stop
        :disabled="func.besoinIsNotModifiable(besoin)"
        :use-html5-validation="false"
        type="number" min="0" step="1"
        ref="inputNbAfficheMaintenance"
      />
    </template>

    <!-- Visuel -->
    <template v-if="field === 'visuel'">
      <div v-if="isDetail" class="is-inline">
        {{ besoin.nomVisuel }}
      </div>
      <b-input
        v-if="!isDetail"
        :value="besoin.nomVisuel"
        @input="updateValue($event, 'nomVisuel', index)"
        :disabled="func.besoinIsNotModifiable(besoin)"
      />
    </template>

    <!-- Commentaire -->
    <template v-if="field === 'commentaire'">
      <div v-if="isDetail" class="is-inline">
        {{ besoin.commentaire }}
      </div>
      <commentaire-list-input
        v-if="!isDetail"
        :id="besoin.key"
        :value="besoin.commentaire"
        @input="updateValue($event, 'commentaire', index)"
        :maj="() => {}"
        :disabled="func.besoinIsNotModifiable(besoin)">
      </commentaire-list-input>
    </template>

    <!-- Tiers destinataire -->
    <template v-if="field === 'tiersDestinataire'">
      <router-link-blank
        v-if="isDetail && besoin.tiersDestinataire"
        :to="{
          name: getTiersRouteName(besoin.tiersDestinataire),
          params: { id: besoin.tiersDestinataireId },
        }"
        :message="besoin.tiersDestinataire.nomCommercial"
        class="is-inline">
      </router-link-blank>
      <tiers-autocomplete
        v-if="!isDetail"
        :value="besoin.tiersDestinataire"
        @input="(event) => {
          updateValue(event, 'tiersDestinataire', index);
          updateValue(event ? event.id : null, 'tiersDestinataireId', index);
        }"
        @click-autocomplete.stop
        :societeActiveId="societeActiveId"
        :type="constantes.TIERS_TYPE_FOURNISSEUR"
        :sousTypes="constantes.SOUS_TYPES"
        :filteredItemFunc="(tiers) => tiers.id !== besoin.tiersDestinataireId"
        :initialValue="besoin.tiersDestinataireId"
        :title-pop-up-advanced-search="`${baseTrad}.advancedSearchFournisseur`"
        :disabled="func.besoinIsNotModifiable(besoin)"
        :tiers-options="tiersInIntervenantsCommande"
        :append-to-body="false"
        :focus="!isNewBesoin"
        search-by-competence-fournisseur
        advanced-search
        expanded>
        <template slot-scope="{ option }">
          <p>
            <span v-if="option.code">{{ option.code }} </span>
            <span>{{ option.nomCommercial }}</span>
          </p>
          <p class="is-size-8 is-italic">
            <span v-if="option.compteComptable">{{ option.compteComptable }} </span>
            <span>{{ option.adressePostaleLibelle }}</span>
          </p>
        </template>
      </tiers-autocomplete>
    </template>

    <!-- Lieu de livraison prestation -->
    <template v-if="field === 'lieuLivraisonPrestation'">
      <router-link-blank
        v-if="isDetail && besoin.lieuLivraisonPrestation"
        :to="{ name: 'c.achat.fournisseurs', params: { id: besoin.lieuLivraisonPrestationId } }"
        :message="besoin.lieuLivraisonPrestation.nomCommercial"
        class="is-inline">
      </router-link-blank>
      <tiers-autocomplete
        v-if="!isDetail"
        :value="besoin.lieuLivraisonPrestation"
        @input="(event) => {
          updateValue(event, 'lieuLivraisonPrestation', index);
          updateValue(event ? event.id : null, 'lieuLivraisonPrestationId', index);
        }"
        @click-autocomplete.stop
        :societeActiveId="societeActiveId"
        :type="constantes.TIERS_TYPE_FOURNISSEUR"
        :sousTypes="[constantes.TIERS_SOUS_TYPE_LIEU_DE_LIVRAISON]"
        :filteredItemFunc="(tiers) => tiers.id !== besoin.lieuLivraisonPrestationId"
        :initialValue="besoin.lieuLivraisonPrestationId"
        :title-pop-up-advanced-search="`${baseTrad}.advancedSearchLieuLivraisonPrestation`"
        :disabled="func.besoinIsNotModifiable(besoin)"
        :append-to-body="false"
        :focus="!isNewBesoin"
        search-by-competence-fournisseur
        advanced-search
        expanded>
        <template slot-scope="{ option }">
          <p>
            <span v-if="option.code">{{ option.code }} </span>
            <span>{{ option.nomCommercial }}</span>
          </p>
          <p class="is-size-8 is-italic">
            <span v-if="option.compteComptable">{{ option.compteComptable }} </span>
            <span>{{ option.adressePostaleLibelle }}</span>
          </p>
        </template>
      </tiers-autocomplete>
    </template>

    <!-- Date de livraison -->
    <template v-if="field === 'dateLivraison'">
      <div v-if="isDetail" class="is-inline">
        {{ func.formatDate(besoin.dateLivraison) }}
      </div>
      <b-datepicker
        v-if="!isDetail"
        :value="besoin.dateLivraison"
        @input="updateValue($event, 'dateLivraison', index)"
        @click.native.stop
        :disabled="func.besoinIsNotModifiable(besoin)"
        ref="datepickerDateLivraison"
        icon="calendar-today"
        position="is-bottom-right"
        nearby-selectable-month-days
        show-week-number editable>
      </b-datepicker>
    </template>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { getAll as getAllFormatAffiche } from '@/api/formatAffiche';
import RouterLinkBlank from '@/components/general/routerLinkBlank';
import QueryAutocomplete from '@/components/general/autocomplete/queryAutocomplete';
import TiersAutocomplete from '@/components/general/autocomplete/tiersAutocomplete';
import ProduitAutocomplete from '@/components/general/autocomplete/produitAutocomplete';
import ParametreAutocomplete from '@/components/general/autocomplete/parametreAutocomplete';
import CommentaireListInput from '@/components/general/input/commentaireListInput/commentaireListInput';
import { RESULTS_PER_PAGE } from '@/configuration/clientTableConf';
import * as devisConf from '@/configuration/devisConf';
import {
  TIERS_TYPE_CLIENT,
  TIERS_TYPE_FOURNISSEUR,
  TIERS_SOUS_TYPE_LIEU_DE_LIVRAISON,
  LISTE_SOUS_TYPE_FOURNISSEURS_PAGES,
} from '@/configuration/tiers/tiersConf';
import {
  CATEGORIE_IMPRESSION_AFFICHE,
  CATEGORIE_PRODUIT_ACHAT,
  CATEGORIE_PRODUIT,
} from '@/configuration/catalogue/produitConf';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import formatDate from '@/services/general/formatDate';
import getAnneeSemaineFromDate from '@/services/general/getAnneeSemaineFromDate';
import { besoinIsNotModifiable, CATEGORIES_REGROUPEMENT } from '@/services/bodac/besoin/besoinService';
import messageInformation from '@/services/general/messageInformation';

const Societe = createNamespacedHelpers('societe');
const Parametres = createNamespacedHelpers('parametres');

export default {
  name: 'BesoinInput',
  props: {
    index: {
      type: Number,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
    field: {
      type: String,
      required: true,
    },
    besoins: {
      type: Array,
      required: true,
    },
    commande: {
      type: Object,
      required: true,
    },
  },
  components: {
    RouterLinkBlank,
    QueryAutocomplete,
    TiersAutocomplete,
    ProduitAutocomplete,
    ParametreAutocomplete,
    CommentaireListInput,
  },
  data() {
    return {
      isDetail: true,
      baseTrad: 'editionCommande.logistiqueImpression.affiches',
      constantes: {
        SENS_ACHAT: devisConf.SENS_ACHAT,
        CATEGORIE_IMPRESSION_AFFICHE,
        TIERS_TYPE_FOURNISSEUR,
        TIERS_SOUS_TYPE_LIEU_DE_LIVRAISON,
        PARAMETRES_KEYS,
        SOUS_TYPES: LISTE_SOUS_TYPE_FOURNISSEURS_PAGES
          .map(el => el.code)
          .filter(el => el !== TIERS_SOUS_TYPE_LIEU_DE_LIVRAISON),
      },
      func: {
        formatDate,
        besoinIsNotModifiable,
      },
    };
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
      societeActive: 'societeActive',
    }),
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    besoin: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    produitsInCommande() {
      return this.commande.lignesCommande
        .filter(l => l.type === devisConf.REGROUPEMENT
          && CATEGORIES_REGROUPEMENT.includes(l.typeProduit)
          && !!l.produit)
        .map(l => l.produit);
    },
    tiersInIntervenantsCommande() {
      return this.commande.intervenants.filter(i => i.tiersId).map(i => i.tiers);
    },
    isNewBesoin() {
      return !this.besoin.id;
    },
    natureSupportAffiche() {
      return this.parametre(PARAMETRES_KEYS.natureSupportAffiche);
    },
    typeMateriel() {
      return this.parametre(PARAMETRES_KEYS.materielType);
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
    }),
    getLibelleAndRoutesForProduct(produit) {
      const { categorieVente } = produit.supportCommercial;
      const config = [
        ...CATEGORIE_PRODUIT_ACHAT,
        ...CATEGORIE_PRODUIT,
      ].find(el => el.code === categorieVente);
      if (!config) return {};
      return {
        name: config.routeDetail,
        params: { id: produit.id },
      };
    },
    async getFormatAfficheData(text) {
      const result = (await getAllFormatAffiche({
        query: { auto: text, societeId: this.societeActiveId },
        limit: RESULTS_PER_PAGE,
      })).data.result.items;
      return result;
    },
    getProduitData(text) {
      return this.produitsInCommande
        .filter(el => el.libelle.toLowerCase().includes(text?.toLowerCase() || ''));
    },
    getTiersRouteName(tiers) {
      if (tiers.type === TIERS_TYPE_CLIENT) return 'c.bodac.clients/contacts-client';
      return 'c.achat.fournisseurs';
    },
    getNatureSupportAffiche(id) {
      return this.natureSupportAffiche.find((n) => n.id === id)?.libelle || '';
    },
    getTypeMateriel(id) {
      return this.typeMateriel.find((t) => t.id === id)?.libelle || '';
    },
    actionOnClick() {
      if (this.isNewBesoin) return;
      if (besoinIsNotModifiable(this.besoin)) {
        messageInformation(this.$t(`${this.baseTrad}.cellCanNotBeEdited`));
        return;
      }
      this.isDetail = !this.isDetail;
    },
    updateValue(event, key, index) {
      if (!index) return;
      this.$set(this.besoins[index - 1], key, event);
      this.$set(this.besoins[index - 1], 'fieldHasModified', this.field);
    },
    updateFormatAfficheValue(val, index) {
      this.updateValue(val || null, 'formatAffiche', index);
      if (!val) return;
      this.updateValue(val.id, 'formatAfficheId', index);
      this.updateValue(val.largeur, 'largeur', index);
      this.updateValue(val.longueur, 'longueur', index);
      this.updateValue(val.natureSupportAfficheId, 'natureSupportAfficheId', index);
      this.updateValue(val.typeMaterielId, 'typeMaterielId', index);
    },
    updateCommentaireValue(id, commentaire) {
      this.updateValue(commentaire, 'commentaire', this.index);
    },
    updateNbAfficheVendueValue(val, index) {
      const valToNumber = parseInt(val || 0, 10);
      this.updateValue(valToNumber, 'nbAfficheVendue', index);
      const { tauxMaintenance } = this.societeActive;
      const maintenance = Math.ceil(valToNumber * (tauxMaintenance / 100));
      this.updateValue(maintenance, 'nbAfficheMaintenance', index);
      const total = valToNumber + maintenance;
      this.updateValue(total, 'nbAfficheTotal', index);
    },
    updateNbAfficheMaintenanceValue(val, index) {
      const valToNumber = parseInt(val || 0, 10);
      this.updateValue(valToNumber, 'nbAfficheMaintenance', index);
      const { nbAfficheVendue } = this.besoins[index - 1];
      const total = valToNumber + (parseInt(nbAfficheVendue || 0, 10) || 0);
      this.updateValue(total, 'nbAfficheTotal', index);
    },
    updateDateDepartValue(dateDepart, index) {
      this.updateValue(dateDepart, 'dateDepart', index);
      this.updateValue(getAnneeSemaineFromDate(dateDepart), 'anneeSemaineDepart', index);
    },
    handleClickOutside(event) {
      const clickedOutside = !this.$refs.besoinInput.contains(event.target);
      if (clickedOutside && !this.isNewBesoin) {
        this.isDetail = true;
        if (this.besoin.fieldHasModified === this.field) this.$emit('update-besoin', this.besoin);
      }
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open({
      container: this.$refs.besoinInput.$el,
    });
    try {
      document.addEventListener('click', this.handleClickOutside);
      await this.loadParametres([
        PARAMETRES_KEYS.materielType,
        PARAMETRES_KEYS.natureSupportAffiche,
      ]);
      this.isDetail = !this.isNewBesoin;
    } finally {
      loadingComponent.close();
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>
